/* eslint-disable no-await-in-loop */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading, Button } from '@brighterion/br-ui';
import { useQueryClient } from '@tanstack/react-query';
import { RQ_GLOBAL_RULES, RQ_MERCHANT_OVERRIDES, handleOnClickEvent, handleKeyEvent } from 'Utils';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import _ from 'lodash';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import PreviewModal from './PreviewModal';
import Uploader from './Uploader';
import styles from './index.css';
import ErrorModal from './ErrorModal';

type Props = {
    uploaderConfig: Record<string, any>;
    rulesTypeText: string;
    rulesType: RulesType;
    specificUploadInstructions?: string;
    previewTitle?: string;
    postFn: (parsedFileData: any, matchedColumnsMap: Record<string, any>) => Promise<void>;
    onCancel: () => void;
    errorContentRenderer: (result: UploadErrorsType) => JSX.Element;
    previewContentRenderer: (parsedFileTableData: ParsedFileTableData) => JSX.Element;
};

export default function UploadWizard({
    uploaderConfig,
    rulesTypeText,
    rulesType,
    specificUploadInstructions,
    previewTitle,
    postFn,
    onCancel,
    errorContentRenderer,
    previewContentRenderer
}: Props) {
    const { t } = useTranslation();
    const { addTechnicalErrorModal } = useReduxModal();
    const queryClient = useQueryClient();

    const [parsedFileTableData, setParsedFileData] = useState<ParsedFileTableData>();
    const [showLoading, setLoading] = useState(false);
    const [tryAgainDisabled, setTryAgainDisabled] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [previewModalStatus, setPreviewModalStatus] = useState(false);
    const [errorModalStatus, setErrorModalStatus] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [result, setResult] = useState<any>();

    const handleTrackEventClick = () => {
        let clickIdentifier = '';
        let text = '';
        switch (rulesType) {
            case 'globalRules': {
                clickIdentifier = 'Global Rules - Bulk Upload';
                text = 'Bulk Upload';
                break;
            }
            case 'merchantOverrides': {
                clickIdentifier = 'Subjects - Bulk Upload';
                text = 'Bulk Upload';
                break;
            }
            default: {
                break;
            }
        }
        if (clickIdentifier && text) {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier,
                    text
                }
            });
        }
    };

    const submit = async () => {
        setTryAgainDisabled(true);
        setLoading(true);
        setPreviewModalStatus(false);
        if (parsedFileTableData) {
            const { data: parsedFileData, matchedColumnsMap }: ParsedFileTableData = parsedFileTableData;
            try {
                await postFn(parsedFileData, matchedColumnsMap);
                handleTrackEventClick();
            } catch (err: any) {
                if (_.isArray(err?.response?.data?.errors)) {
                    setSubmitError(err);
                    setResult(err.response.data);
                    setErrorModalStatus(true);
                } else {
                    addTechnicalErrorModal();
                }
            } finally {
                setLoading(false);
                queryClient.invalidateQueries([RQ_GLOBAL_RULES]);
                queryClient.invalidateQueries([RQ_MERCHANT_OVERRIDES]);
            }
        }
    };

    useEffect(() => {
        if (tryAgainDisabled && submitError != null) {
            setTryAgainDisabled(false);
        }
    }, [tryAgainDisabled, submitError]);

    const preview = () => {
        setSubmitError(null);
        setPreviewModalStatus(true);
    };

    if (!uploaderConfig) return null;

    return (
        <div className={styles.SubPage}>
            <div className={styles.FormContainer}>
                {showLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loading}>
                            <div data-testid='loading-text'>{`${t('Please wait while we process your file')}...`}</div>
                            <div className={styles.spinnerContainer} data-testid='loading-spinner'>
                                <Loading size='xlg' fillName='dark-orange' />
                            </div>
                        </div>
                    </div>
                )}
                <Uploader
                    uploaderConfig={uploaderConfig}
                    setParsedFileData={setParsedFileData}
                    rulesTypeText={rulesTypeText}
                    rulesType={rulesType}
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    specificUploadInstructions={specificUploadInstructions}
                />
                <div className={styles.buttonsRow}>
                    <Button
                        variant='tertiary'
                        size='small'
                        data-testid='cancel-btn'
                        onClick={handleOnClickEvent(onCancel)}
                        onKeyDown={handleKeyEvent(onCancel)}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant='primary'
                        size='small'
                        data-testid='upload-btn'
                        onClick={handleOnClickEvent(preview)}
                        onKeyDown={handleKeyEvent(preview)}
                        disabled={!parsedFileTableData}
                    >
                        {t('Preview File')}
                    </Button>
                </div>
                <PreviewModal
                    previewModalStatus={previewModalStatus}
                    parsedFileTableData={parsedFileTableData}
                    setPreviewModalStatus={setPreviewModalStatus}
                    submit={submit}
                    uploadedFile={uploadedFile}
                    rulesTypeText={rulesTypeText}
                    previewTitle={previewTitle}
                    previewContentRenderer={previewContentRenderer}
                />
                <ErrorModal
                    result={result}
                    onClose={() => {
                        setErrorModalStatus(false);
                    }}
                    uploadedFile={uploadedFile as File}
                    isOpen={errorModalStatus}
                    parsedFileTableData={parsedFileTableData}
                    errorContentRenderer={errorContentRenderer}
                    rulesType={rulesType}
                />
            </div>
        </div>
    );
}
